/* ──────────────────────  CustomerMenu.jsx (Fixed Unit Dropdown + Search)  ────────────────────── */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useStore } from '../store/useStore';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, Chip, IconButton, Stack, Typography,
  Button, TextField, Paper, Alert, Select, MenuItem, FormControl,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, CardContent
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import {
  AddShoppingCart, Remove, Add, Delete, CheckCircle, AccessTime, LocalDining,
  TableBar, LocationOn, Phone as PhoneIcon, Search, Clear
} from '@mui/icons-material';

// === LUXURY DARK GOLD THEME ===
const theme = createTheme({
  palette: {
    primary: { main: '#d4af37' },
    secondary: { main: '#b8932b' },
    background: { default: '#0f0a00', paper: 'rgba(255,255,255,0.05)' },
    text: { primary: '#ffffff', secondary: '#d4af37' },
    success: { main: '#28a745' },
    error: { main: '#d32f2f' },
  },
  typography: {
    fontFamily: '"Playfair Display", "Inter", "Poppins", serif',
    h4: { fontWeight: 900, letterSpacing: '0.5px' },
    h5: { fontWeight: 800 },
    body1: { fontWeight: 500 },
  },
  shape: { borderRadius: 36 },
});

// === GOLDEN PARTICLE BACKGROUND ===
const Particles = styled(Box)({
  position: 'fixed',
  top: 0, left: 0, right: 0, bottom: 0,
  pointerEvents: 'none',
  zIndex: 0,
  '&::before': {
    content: '""',
    position: 'absolute',
    width: '100%', height: '100%',
    background: `
      radial-gradient(circle at 20% 80%, rgba(212,175,55,0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(212,175,55,0.15) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(212,175,55,0.1) 0%, transparent 50%)
    `,
    animation: 'pulse 8s infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 0.6 },
    '50%': { opacity: 1 },
  },
});

// === ULTRA LUXURY HEADER CARD ===
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'rgba(15, 10, 0, 0.4)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(212, 175, 55, 0.4)',
  borderRadius: 36,
  padding: '32px 24px',
  boxShadow: `
    0 20px 40px rgba(0,0,0,0.3),
    0 0 40px rgba(212,175,55,0.2),
    inset 0 1px 0 rgba(255,255,255,0.1)
  `,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: 6,
    background: 'linear-gradient(90deg, #d4af37, #b8932b, #d4af37)',
    backgroundSize: '300% 100%',
    animation: 'shimmer 3s infinite',
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '0% 50%' },
    '100%': { backgroundPosition: '300% 50%' },
  },
}));

// === 3D FLOATING LOGO (CENTERED) ===
const LogoBox = styled(Box)(({ theme }) => ({
  width: 90,
  height: 90,
  borderRadius: '28px',
  overflow: 'hidden',
  border: '5px solid #d4af37',
  background: '#ffffff',
  boxShadow: `
    0 16px 40px rgba(0,0,0,0.3),
    0 0 0 10px rgba(212,175,55,0.3),
    inset 0 4px 10px rgba(0,0,0,0.1)
  `,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
  transition: 'all 0.4s ease',
  '& img': { width: '100%', height: '100%', objectFit: 'cover' },
  '&:hover': {
    transform: 'translateY(-8px) scale(1.08)',
    boxShadow: '0 28px 60px rgba(0,0,0,0.4), 0 0 0 14px rgba(212,175,55,0.4)',
  },
}));

// === GOLDEN SHIMMER TEXT ===
const GoldText = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(90deg, #d4af37, #fff, #d4af37, #b8932b, #d4af37)',
  backgroundSize: '300% 100%',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  fontWeight: 900,
  textShadow: '0 2px 8px rgba(212,175,55,0.4)',
  animation: 'textShine 5s infinite',
  textAlign: 'center',
  '@keyframes textShine': {
    '0%': { backgroundPosition: '0% 50%' },
    '100%': { backgroundPosition: '300% 50%' },
  },
}));

// === ELEGANT TABLE CHIP (CENTERED) ===
const TableChip = styled(Chip)(({ theme }) => ({
  bgcolor: 'rgba(212,175,55,0.2)',
  color: '#d4af37',
  fontWeight: 800,
  fontSize: '1.1rem',
  height: 44,
  px: 2.5,
  borderRadius: 24,
  border: '2px solid #d4af37',
  boxShadow: '0 6px 16px rgba(212,175,55,0.3)',
  backdropFilter: 'blur(8px)',
  margin: '0 auto',
  '& .MuiChip-icon': { color: '#d4af37', fontSize: 22 },
}));

// === MENU ITEM & BUTTONS ===
const ItemPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  padding: '24px',
  background: 'rgba(255,255,255,0.08)',
  border: '1px solid rgba(212,175,55,0.3)',
  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
    borderColor: 'rgba(212,175,55,0.5)',
    transform: 'translateY(-4px)',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: 'none',
  fontWeight: 700,
  color: '#ffffff',
  fontSize: '1rem',
  padding: '14px 28px',
  boxShadow: '0 8px 20px rgba(212,175,55,0.4)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 28px rgba(212,175,55,0.5)',
  },
}));

export default function CustomerMenu() {
  const { setCustomerContext, cart, addToCart, updateQty, removeFromCart, clearCart } = useStore();

  const [menu, setMenu] = useState([]);
  const [restaurantName, setRestaurantName] = useState('Restaurant');
  const [restaurantLogo, setRestaurantLogo] = useState('');
  const [restaurantAddress, setRestaurantAddress] = useState('');
  const [restaurantPhone, setRestaurantPhone] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [orderStatus, setOrderStatus] = useState('');
  const [selectedUnit, setSelectedUnit] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showRefreshWarning, setShowRefreshWarning] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // Search state

  const { restaurantId: rid, tableId: tid } = useParams();
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('s');
  const navigate = useNavigate();
  const api = process.env.REACT_APP_API_URL;

  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const REFRESH_WARNING_KEY = 'refreshWarningShown';
  const ORDER_ID_KEY = 'currentOrderId';

  /* ──────  Load menu & restaurant profile  ────── */
  useEffect(() => {
    if (!rid || !tid || !urlToken) {
      setError('Invalid QR code.');
      setLoading(false);
      return;
    }
    const restaurantId = +rid, tableId = +tid;
    if (isNaN(restaurantId) || isNaN(tableId)) {
      setError('Invalid IDs.');
      setLoading(false);
      return;
    }

    setCustomerContext(restaurantId, tableId, urlToken, '');
    loadMenu(restaurantId, tableId, urlToken);

    const storedOrderId = localStorage.getItem(ORDER_ID_KEY);
    if (storedOrderId) checkExistingOrder(+storedOrderId);
  }, [rid, tid, urlToken, setCustomerContext]);

  const loadMenu = async (restaurantId, tableId, token) => {
    try {
      const { data } = await axios.get(
        `${api}/index.php/restaurants/${restaurantId}/menu`,
        { params: { table_id: tableId, token } }
      );

      const { menu: raw, tableNumber: tn, restaurant_name, logo_url, address, phone } = data;

      if (!Array.isArray(raw)) throw new Error('Bad menu');

      setMenu(raw);
      setRestaurantName(restaurant_name || 'Restaurant');
      setRestaurantLogo(logo_url ? `${api}/${logo_url}` : '');
      setRestaurantAddress(address || '');
      setRestaurantPhone(phone || '');
      setTableNumber(tn || `Table ${tableId}`);
    } catch (e) {
      setError('Failed to load menu. QR may be expired.');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingOrder = async (orderId) => {
    try {
      const { data } = await axios.get(`${api}/check-existing-order.php`, {
        params: { order_id: orderId }
      });
      if (data?.hasOrder) {
        setShowRefreshWarning(true);
        sessionStorage.setItem(REFRESH_WARNING_KEY, 'true');
        setOrderPlaced({ id: orderId, status: data.status });
        setOrderStatus(data.status);
      } else {
        localStorage.removeItem(ORDER_ID_KEY);
      }
    } catch { }
  };

  /* ──────  Order status polling  ────── */
  useEffect(() => {
    if (!orderPlaced) return;
    const iv = setInterval(async () => {
      try {
        const { data } = await axios.get(`${api}/check-order-status.php`, {
          params: { order_id: orderPlaced.id, token: urlToken }
        });
        setOrderStatus(data.status);
        if (['ready', 'paid'].includes(data.status)) clearInterval(iv);
      } catch { }
    }, 8000);
    return () => clearInterval(iv);
  }, [orderPlaced, urlToken]);

  const placeOrder = async () => {
    const name = customerName.trim() || 'Guest';
    const payload = {
      tableId: +tid,
      token: urlToken,
      customerName: name,
      items: cart.map(i => ({
        itemId: i.id,
        qty: i.qty,
        unit: i.unit,
        notes: i.notes || ''
      }))
    };
    try {
      const { data } = await axios.post(`${api}/place-order.php`, payload);
      const newOrderId = data.orderId;

      localStorage.setItem(ORDER_ID_KEY, newOrderId);
      setOrderPlaced({ id: newOrderId, status: 'pending' });
      setOrderStatus('pending');
      clearCart();
      setCustomerName('');
    } catch (e) {
      alert(e.response?.data?.error || 'Order failed');
    }
  };

  const handleCreateNewOrder = () => {
    localStorage.removeItem(ORDER_ID_KEY);
    sessionStorage.removeItem(REFRESH_WARNING_KEY);
    setOrderPlaced(null);
    setOrderStatus('');
    setShowRefreshWarning(false);
  };

  const getUnits = (item) => {
    const list = [];
    if (item.price_half > 0) list.push({ v: 'half', l: `Half - ₹${item.price_half}` });
    if (item.price_full > 0) list.push({ v: 'full', l: `Full - ₹${item.price_full}` });
    if (item.price_per_piece > 0) list.push({ v: 'piece', l: `Piece - ₹${item.price_per_piece}` });
    return list.length ? list : [{ v: 'full', l: 'N/A' }];
  };

  const priceFor = (item, unit) => {
    return unit === 'half' ? item.price_half
      : unit === 'full' ? item.price_full
      : unit === 'piece' ? item.price_per_piece
      : item.price_full || item.price_half || 0;
  };

  const handleAdd = (item) => {
    const unit = selectedUnit[item.id] || item.unit_type || getUnits(item)[0].v;
    const price = priceFor(item, unit);
    addToCart({ ...item, unit, price });
  };

  const openMaps = () => {
    const query = encodeURIComponent(restaurantAddress);
    window.open(`https://maps.google.com/?q=${query}`, '_blank');
  };

  const callPhone = () => {
    window.location.href = `tel:${restaurantPhone}`;
  };

  // FIXED: Render menu item with visible dropdown
  const renderMenuItem = (item) => {
    const units = getUnits(item);
    const cur = selectedUnit[item.id] || item.unit_type || units[0].v;

    return (
      <ItemPaper key={item.id} elevation={0}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#fff' }}>
          {item.name}
          {searchTerm && (
            <Chip
              label={item.category}
              size="small"
              sx={{ ml: 1, bgcolor: 'rgba(212,175,55,0.3)', color: '#d4af37', fontWeight: 600 }}
            />
          )}
        </Typography>
        <Typography variant="body2" color="#ccc" sx={{ mb: 2, fontSize: '0.95rem' }}>
          {units.map(u => u.l).join(' • ')}
        </Typography>

        <Stack direction="column" spacing={2}>
          <FormControl fullWidth size="small">
            <Select
              value={cur}
              onChange={e => setSelectedUnit({ ...selectedUnit, [item.id]: e.target.value })}
              disabled={!item.is_available || orderPlaced}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#1a1a1a',
                    color: '#fff',
                    '& .MuiMenuItem-root': {
                      fontSize: '0.95rem',
                      color: '#fff',
                      '&:hover': { bgcolor: '#d4af37', color: '#000' },
                      '&.Mui-selected': { bgcolor: '#b8932b', color: '#fff' },
                    },
                  },
                },
              }}
              sx={{
                color: '#fff',
                fontWeight: 600,
                bgcolor: 'rgba(255,255,255,0.1)',
                borderRadius: 3,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(212,175,55,0.6)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d4af37',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#d4af37',
                  boxShadow: '0 0 0 1px #d4af37',
                },
                '& .MuiSvgIcon-root': { color: '#d4af37' },
                '& .MuiSelect-select': {
                  py: 1.5,
                  fontSize: '0.95rem',
                },
              }}
            >
              {units.map(u => (
                <MenuItem key={u.v} value={u.v}>
                  {u.l}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <ActionButton
            fullWidth
            startIcon={<AddShoppingCart />}
            onClick={() => handleAdd(item)}
            disabled={!item.is_available || orderPlaced}
            sx={{
              bgcolor: item.is_available && !orderPlaced ? '#d4af37' : '#555',
              color: '#fff',
              '&:disabled': { color: '#aaa' }
            }}
          >
            Add to Order
          </ActionButton>
        </Stack>
      </ItemPaper>
    );
  };

  if (loading) return (
    <Box sx={{ textAlign: 'center', p: 8, bgcolor: '#0f0a00', minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Typography variant="h5" sx={{ color: '#d4af37', fontWeight: 800 }}>Loading your experience...</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ textAlign: 'center', p: 8, bgcolor: '#0f0a00', minHeight: '100vh' }}>
      <Alert severity="error" sx={{ bgcolor: 'rgba(255,0,0,0.1)', color: '#fff', border: '1px solid #d32f2f' }}>
        {error}
      </Alert>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <Particles />
      <Box sx={{ minHeight: '100vh', bgcolor: '#0f0a00', position: 'relative', overflow: 'hidden' }}>

        {/* LUXURY HEADER */}
        <HeaderCard sx={{ mb: 4, mx: { xs: 2, sm: 3 }, mt: 2 }}>
          <Stack direction="column" alignItems="center" spacing={2.5} sx={{ textAlign: 'center' }}>
            {restaurantLogo ? (
              <LogoBox>
                <img src={restaurantLogo} alt={restaurantName} />
              </LogoBox>
            ) : (
              <LogoBox sx={{ bgcolor: '#d4af37', color: '#0f0a00', fontWeight: 900, fontSize: '2.2rem' }}>
                {restaurantName.charAt(0)}
              </LogoBox>
            )}
            <GoldText variant="h4" sx={{ fontSize: { xs: '1.9rem', sm: '2.5rem' }, lineHeight: 1.1 }}>
              {restaurantName}
            </GoldText>
            {restaurantAddress && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ color: '#d4af37', cursor: 'pointer', '&:hover': { color: '#fff' }, transition: 'color 0.3s' }}
                onClick={openMaps}
              >
                <LocationOn sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>{restaurantAddress}</Typography>
              </Stack>
            )}
            <TableChip icon={<TableBar />} label={tableNumber} />
            {restaurantPhone && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ color: '#ccc', cursor: 'pointer', '&:hover': { color: '#fff' }, transition: 'color 0.3s', mt: 1 }}
                onClick={callPhone}
              >
                <PhoneIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>{restaurantPhone}</Typography>
              </Stack>
            )}
          </Stack>
        </HeaderCard>

        {/* SEARCH BAR */}
        <Box sx={{ px: { xs: 2, sm: 3 }, mb: 2 }}>
          <Paper
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(212,175,55,0.4)',
              borderRadius: 36,
              overflow: 'hidden',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search menu items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { border: 'none' },
                },
                '& .MuiInputBase-input::placeholder': { color: '#aaa', opacity: 1 },
              }}
            />
            <IconButton
              onClick={() => setSearchTerm('')}
              sx={{ color: '#d4af37', mr: 1 }}
              disabled={!searchTerm}
            >
              {searchTerm ? <Clear /> : <Search />}
            </IconButton>
          </Paper>
        </Box>

        {/* Order Status */}
        {orderPlaced && (
          <Alert
            severity={orderStatus === 'ready' ? 'success' : 'info'}
            icon={orderStatus === 'ready' ? <CheckCircle /> : <AccessTime />}
            sx={{ mx: 3, mt: 1, mb: 2, fontSize: '0.95rem', bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(212,175,55,0.4)' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#fff' }}>
              Order #{orderPlaced.id}
            </Typography>
            <Chip label={statusText[orderStatus] || 'Unknown'} size="small" sx={{ mt: 0.5, bgcolor: '#d4af37', color: '#fff' }} />
            <Typography variant="body2" sx={{ mt: 0.5, color: '#ccc' }}>
              {statusMsg[orderStatus] || ''}
            </Typography>
          </Alert>
        )}

        {/* Tabs – hidden when searching */}
        {!searchTerm && (
          <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#ccc',
                  minHeight: 60,
                  '&.Mui-selected': { color: '#d4af37' }
                },
                '& .MuiTabs-indicator': { backgroundColor: '#d4af37', height: 3 }
              }}
            >
              {menu.map((cat, idx) => (
                <Tab key={cat.id} label={cat.name} id={`tab-${idx}`} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Tab Panel / Search Results */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          {menu.length === 0 ? (
            <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 4, bgcolor: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(212,175,55,0.4)' }}>
              <Typography color="text.secondary">No items available</Typography>
            </Paper>
          ) : searchTerm ? (
            <Stack spacing={3}>
              {menu
                .flatMap(cat => cat.items.map(i => ({ ...i, category: cat.name })))
                .filter(item =>
                  item.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(item => renderMenuItem(item))}
            </Stack>
          ) : (
            <Box role="tabpanel" hidden={tabValue !== menu.findIndex(c => c.id === menu[tabValue]?.id)}>
              {menu[tabValue] && (
                <Stack spacing={3}>
                  {menu[tabValue].items.length === 0 ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No items in this category
                    </Typography>
                  ) : (
                    menu[tabValue].items.map(renderMenuItem)
                  )}
                </Stack>
              )}
            </Box>
          )}
        </Box>

        {/* Cart Bottom Bar */}
        {(cart.length > 0) && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'rgba(15,10,0,0.8)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.4)',
              p: 2,
              borderTop: '1px solid rgba(212,175,55,0.4)',
              zIndex: 1000
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff' }}>Total</Typography>
              <GoldText sx={{ fontWeight: 800, fontSize: '1.4rem' }}>
                ₹{total.toFixed(2)}
              </GoldText>
            </Box>

            <ActionButton
              fullWidth
              size="large"
              variant="contained"
              onClick={() => setPreviewOpen(true)}
              sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
            >
              Place Order ({cart.reduce((s, i) => s + i.qty, 0)} items)
            </ActionButton>
          </Box>
        )}
        {cart.length > 0 && <Box sx={{ height: 90 }} />}

        {/* Thank You */}
        {orderPlaced && cart.length === 0 && (
          <Paper sx={{ p: 6, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.08)', mb: 3, mx: 3, border: '1px solid rgba(212,175,55,0.4)' }}>
            <LocalDining sx={{ fontSize: 64, color: '#d4af37', mb: 2 }} />
            <GoldText variant="h5" sx={{ fontWeight: 800 }}>
              Thank You!
            </GoldText>
            <Typography sx={{ mt: 1, color: '#ccc' }}>
              Your order has been sent.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <ActionButton
                variant="contained"
                size="large"
                onClick={handleCreateNewOrder}
                sx={{
                  bgcolor: '#28a745',
                  '&:hover': { bgcolor: '#218838' },
                  fontWeight: 600,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                Create New Order
              </ActionButton>
            </Box>
          </Paper>
        )}

        {/* Order Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} fullScreen PaperProps={{ sx: { bgcolor: '#0f0a00', m: 0 } }}>
          <DialogTitle sx={{ textAlign: 'center', py: 3, bgcolor: 'rgba(15,10,0,0.6)', borderBottom: '1px solid rgba(212,175,55,0.4)' }}>
            <GoldText variant="h5" sx={{ fontWeight: 800 }}>Order Preview</GoldText>
            <Typography variant="body2" sx={{ color: '#d4af37', mt: 0.5, fontWeight: 600 }}>
              {tableNumber} • {restaurantName}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#0f0a00' }}>
            <Stack spacing={3}>
              {cart.map((item) => {
                const lineTotal = item.price * item.qty;
                return (
                  <ItemPaper key={`${item.id}-${item.unit}`} elevation={0} style={{ marginTop: '20px' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff' }}>
                            {item.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip label={item.unit} size="small" sx={{ bgcolor: '#d4af37', color: '#fff', fontWeight: 600, textTransform: 'uppercase' }} />
                            <Typography variant="body2" color="#ccc">
                              ₹{item.price} each
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton size="small" color="error" onClick={() => { removeFromCart(item.id, item.unit); if (cart.length === 1) setPreviewOpen(false); }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" onClick={() => updateQty(item.id, item.qty - 1, item.unit)} disabled={item.qty <= 1} sx={{ bgcolor: 'rgba(255,255,0.1)', width: 36, height: 36 }}>
                            <Remove fontSize="small" />
                          </IconButton>
                          <Typography sx={{ minWidth: 36, textAlign: 'center', fontWeight: 700, fontSize: '1.2rem', color: '#fff' }}>
                            {item.qty}
                          </Typography>
                          <IconButton size="small" onClick={() => updateQty(item.id, item.qty + 1, item.unit)} sx={{ bgcolor: '#d4af37', color: '#fff', width: 36, height: 36 }}>
                            <Add fontSize="small" />
                          </IconButton>
                        </Box>
                        <GoldText sx={{ fontWeight: 800, fontSize: '1.2rem' }}>
                          ₹{lineTotal.toFixed(2)}
                        </GoldText>
                      </Box>
                    </CardContent>
                  </ItemPaper>
                );
              })}

              <Box sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 28, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', border: '1px solid rgba(212,175,55,0.4)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>Grand Total</Typography>
                  <GoldText variant="h6" sx={{ fontWeight: 800 }}>
                    ₹{total.toFixed(2)}
                  </GoldText>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Your Name (optional)"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                variant="outlined"
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 16,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: '#fff',
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#d4af37' }
                  },
                  '& .MuiInputLabel-root': { color: '#ccc' }
                }}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 3, bgcolor: 'rgba(15,10,0,0.8)', flexDirection: 'column', gap: 2, borderTop: '1px solid rgba(212,175,55,0.4)' }}>
            <ActionButton
              fullWidth
              size="large"
              variant="contained"
              onClick={() => { setPreviewOpen(false); placeOrder(); }}
              sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, fontSize: '1.1rem', py: 1.5 }}
            >
              Confirm & Place Order
            </ActionButton>
            <Button fullWidth onClick={() => setPreviewOpen(false)} sx={{ color: '#ccc', textTransform: 'none', fontWeight: 600 }}>
              Back to Menu
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </ThemeProvider>
  );
}

/* ──────  Status Helpers ────── */
const statusText = { pending: 'Pending', accepted: 'Accepted', preparing: 'Preparing', ready: 'Ready', paid: 'Paid' };
const statusMsg = {
  pending: 'Waiting for kitchen...',
  accepted: 'Chef is on it!',
  preparing: 'Almost ready...',
  ready: 'Your order is ready! Please collect.'
};